<!--
  The mix design sheet.

  Field order mirrors the printed CC520N document, because the people filling
  it in know that document. What the printed sheet's letterhead carries — the
  addressee, the job number, the date — is deliberately not here: a mix design
  in this library is company-wide, not a letter to one customer.

  Saving is explicit. This is an office screen on a desk, not a field screen in
  the rain, so there is no per-section autosave and no device draft: the one
  Save button writes the whole document, which is also how the API takes it.
-->
<script lang="ts">
  import { api, isApiError } from '../lib/api';
  import { mixMassTotalKg } from '../lib/calc';
  import { printMixDesign } from '../lib/export/mix-design-pdf';
  import { navigate, routePath } from '../lib/router';
  import { MIX_DESIGN_DEFAULT_MATERIALS } from '../lib/seed-data';
  import { notify, notifyError, toInput, toNumber } from '../lib/store';
  import type { MixDesignMaterialInput, MixMaterialUnit, SaveMixDesignRequest } from '../lib/types';

  interface Props {
    /** null for a new design. */
    mixId?: number | null;
  }

  let { mixId = null }: Props = $props();

  const UNITS: MixMaterialUnit[] = ['kg', 'L', 'mL', '%'];

  /**
   * A materials-table row as held by the form. `density_kg_m3` and
   * `volume_m3` are the raw text as typed, kept as strings — the same
   * pattern already used for `wcRatio`, `wcRatioMax` and `yieldM3` below.
   * A two-way bind straight to a `number | null` would round-trip every
   * keystroke through `toNumber`/`toInput`, and any value that does not
   * parse yet ('-', a leading '.') would be erased as it is typed. These
   * are converted to `number | null` only in body(), where the request is
   * assembled; the wire type (`MixDesignMaterialInput`) is unchanged.
   */
  interface MaterialRow {
    name: string;
    amount: string;
    unit: MixMaterialUnit;
    density_kg_m3: string;
    volume_m3: string;
  }

  let loading = $state(mixId !== null);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The id once saved; a new design gets one on its first save. */
  let savedId = $state<number | null>(mixId);

  let projectName = $state('');
  let mixCode = $state('');
  let subject = $state('');
  let slump = $state('');
  let specifiedStrength = $state('');
  let wcRatio = $state('');
  let wcRatioMax = $state('');
  let yieldM3 = $state('1');
  let notes = $state('');

  /** A blank sheet starts from the standard SCI rows. */
  function startingMaterials(): MaterialRow[] {
    return MIX_DESIGN_DEFAULT_MATERIALS.map((row) => ({
      name: row.name,
      amount: '',
      unit: row.unit,
      density_kg_m3: '',
      volume_m3: ''
    }));
  }

  let materials = $state<MaterialRow[]>(mixId === null ? startingMaterials() : []);

  // mixMassTotalKg only reads name/amount/unit; density and volume are not
  // masses, so they are left off this mapping rather than parsed for it.
  const massTotal = $derived(
    mixMassTotalKg(materials.map(({ name, amount, unit }) => ({ name, amount, unit })))
  );

  $effect(() => {
    if (mixId === null) return;
    loading = true;
    api
      .getMixDesign(mixId)
      .then((response) => {
        const design = response.mix_design;
        projectName = design.project_name;
        mixCode = design.mix_code;
        subject = design.subject;
        slump = design.slump;
        specifiedStrength = design.specified_strength;
        wcRatio = toInput(design.wc_ratio);
        wcRatioMax = toInput(design.wc_ratio_max);
        yieldM3 = toInput(design.yield_m3);
        notes = design.notes;
        materials = response.materials.map((row) => ({
          name: row.name,
          amount: row.amount,
          unit: row.unit,
          density_kg_m3: toInput(row.density_kg_m3),
          volume_m3: toInput(row.volume_m3)
        }));
        savedId = design.id;
      })
      .catch((cause) => {
        notifyError(cause, 'Could not load that mix design');
        error = 'Could not load that mix design.';
      })
      .finally(() => {
        loading = false;
      });
  });

  function addMaterial() {
    materials = [...materials, { name: '', amount: '', unit: 'kg', density_kg_m3: '', volume_m3: '' }];
  }

  function removeMaterial(index: number) {
    materials = materials.filter((_, i) => i !== index);
  }

  function moveMaterial(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= materials.length) return;
    const next = [...materials];
    [next[index], next[target]] = [next[target], next[index]];
    materials = next;
  }

  /** Converts the form's string-held rows to the wire shape, in one place. */
  function materialsForSave(): MixDesignMaterialInput[] {
    return materials.map((row) => ({
      name: row.name,
      amount: row.amount,
      unit: row.unit,
      density_kg_m3: toNumber(row.density_kg_m3),
      volume_m3: toNumber(row.volume_m3)
    }));
  }

  function body(): SaveMixDesignRequest {
    return {
      project_name: projectName,
      mix_code: mixCode,
      subject,
      slump,
      specified_strength: specifiedStrength,
      wc_ratio: toNumber(wcRatio),
      wc_ratio_max: toNumber(wcRatioMax),
      yield_m3: toNumber(yieldM3) ?? 1,
      notes,
      materials: materialsForSave()
    };
  }

  let printing = $state(false);

  /**
   * Print what is on screen, not what was last saved: the author expects the
   * document to match the form in front of them. Ids and timestamps are
   * placeholders because the builder does not print them.
   */
  function print() {
    if (printing) return;
    printing = true;
    const stamp = new Date().toISOString();
    const started = printMixDesign(
      {
        mix_design: {
          id: savedId ?? 0,
          project_name: projectName,
          mix_code: mixCode,
          subject,
          slump,
          specified_strength: specifiedStrength,
          wc_ratio: toNumber(wcRatio),
          wc_ratio_max: toNumber(wcRatioMax),
          yield_m3: toNumber(yieldM3) ?? 1,
          notes,
          created_at: stamp,
          updated_at: stamp
        },
        materials: materials.map((material, index) => ({
          id: index + 1,
          mix_design_id: savedId ?? 0,
          seq: index + 1,
          name: material.name,
          amount: material.amount,
          unit: material.unit,
          density_kg_m3: toNumber(material.density_kg_m3),
          volume_m3: toNumber(material.volume_m3),
          created_at: stamp,
          updated_at: stamp
        })),
        exported_at: stamp
      },
      {
        onDone: (cause) => {
          printing = false;
          if (cause) {
            notify({
              tone: 'warn',
              title: 'Could not open the print view',
              detail: 'Try again, or use your browser’s own Print command.',
              sticky: true
            });
          }
        }
      }
    );

    if (!started) {
      printing = false;
      notify({
        tone: 'warn',
        title: 'This browser will not print',
        detail: 'Use your browser’s own Print command instead.',
        sticky: true
      });
    }
  }

  async function save() {
    if (saving) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const response =
        savedId === null
          ? await api.createMixDesign(body())
          : await api.updateMixDesign(savedId, body());

      const isNew = savedId === null;
      savedId = response.mix_design.id;
      // yield_m3 has a server-side fallback (an empty field is saved as 1).
      // On an update the component does not remount, so without this the
      // field would keep showing what the user cleared it to while the
      // record actually holds the fallback. Only this one value is written
      // back — not the rest of the response — so a save cannot clobber
      // other fields the user is still editing while the request is inflight.
      yieldM3 = toInput(response.mix_design.yield_m3);
      notify({ tone: 'ok', title: isNew ? 'Mix design created' : 'Mix design saved' });
      // A new design gets its own address, so a refresh or a back button lands
      // on the saved sheet rather than a blank one.
      if (isNew) navigate(routePath.mixDesign(savedId), { replace: true, keepScroll: true });
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /**
   * Delete, behind an in-page confirmation step rather than a confirm() —
   * the app uses no modal dialogs anywhere.
   */
  let confirmingDelete = $state(false);

  async function remove() {
    if (savedId === null || deleting) return;
    deleting = true;
    try {
      await api.deleteMixDesign(savedId);
      notify({ tone: 'ok', title: 'Mix design deleted' });
      navigate(routePath.mixes(), { replace: true });
    } catch (cause) {
      notifyError(cause, 'Could not delete that mix design');
    } finally {
      deleting = false;
      confirmingDelete = false;
    }
  }
</script>

{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Could not save</strong>{error}</div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <h1 class="card__title">{savedId === null ? 'New mix design' : mixCode || 'Mix design'}</h1>

      <label class="field">
        <span class="label">Project name</span>
        <input type="text" bind:value={projectName} placeholder="Secant Pile Walls" />
      </label>

      <label class="field">
        <span class="label">Mix code</span>
        <input type="text" bind:value={mixCode} placeholder="CC520N" />
        {#if fieldErrors.mix_code}<span class="field__flag">{fieldErrors.mix_code}</span>{/if}
      </label>

      <label class="field">
        <span class="label">Subject</span>
        <input
          type="text"
          bind:value={subject}
          placeholder="Cement Only 5 MPa @ 7 days, 20mm MSA, N exposure class"
        />
        <span class="field__hint">The specification line from the top of the sheet.</span>
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Materials</h2>

      <!--
        Below 60rem (see the style block) a row wide enough for six
        columns — name, amount, unit, density, volume, actions — cannot fit
        without side-scrolling, and narrowing the columns instead just makes
        density and volume too cramped to read. So each material stacks into
        its own block there, with the small fields flowing beneath the name
        in a grid that wraps further as the width keeps shrinking.

        At and above that width the row fits on one line, which is what a
        mix design sheet is *for* — you can see the whole mix at once instead
        of scrolling seven repeated MATERIAL/SSD PROPORTION/... blocks. The
        markup is identical at both sizes; only the CSS toggles, via
        display:grid vs. display:block and the .material__fields wrapper
        switching to display:contents so its four children join the row's
        own grid columns directly. The .materials__head row supplies the
        one-time column headings above that width; each field's aria-label
        is what keeps it identifiable once its per-row caption is hidden
        there, since the heading is no longer adjacent to it in the DOM.
      -->
      <div class="materials">
        <div class="materials__head" aria-hidden="true">
          <span>Material</span>
          <span>SSD proportion</span>
          <span>Unit</span>
          <span>Density (kg/m³)</span>
          <span>Volume (m³)</span>
          <span></span>
        </div>

        {#each materials as material, index (index)}
          <div class="material">
            <label class="field material__name">
              <span class="label">Material</span>
              <input
                type="text"
                bind:value={material.name}
                aria-label="Material {index + 1} name"
                placeholder="Material name"
              />
            </label>

            <div class="material__fields">
              <label class="material__field">
                <span class="label">SSD proportion</span>
                <!-- Text, not number: '1-4' is a real amount on this sheet. -->
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={material.amount}
                  aria-label="Material {index + 1} amount"
                />
              </label>

              <label class="material__field">
                <span class="label">Unit</span>
                <select bind:value={material.unit} aria-label="Material {index + 1} unit">
                  {#each UNITS as unit (unit)}
                    <option value={unit}>{unit}</option>
                  {/each}
                </select>
              </label>

              <label class="material__field">
                <span class="label">Density (kg/m³)</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={material.density_kg_m3}
                  aria-label="Material {index + 1} density"
                />
              </label>

              <label class="material__field">
                <span class="label">Volume (m³)</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={material.volume_m3}
                  aria-label="Material {index + 1} volume"
                />
              </label>
            </div>

            <div class="material__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveMaterial(index, -1)}
                disabled={index === 0}
                aria-label="Move {material.name || `row ${index + 1}`} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveMaterial(index, 1)}
                disabled={index === materials.length - 1}
                aria-label="Move {material.name || `row ${index + 1}`} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeMaterial(index)}
                aria-label="Remove {material.name || `row ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}

        <div class="material material--total">
          <div class="material__fields">
            <div class="material__field">
              <span class="label">Total mass</span>
              <p class="material__total mono">{massTotal} kg</p>
            </div>
            <label class="material__field">
              <span class="label">Yield (m³)</span>
              <input type="text" inputmode="decimal" bind:value={yieldM3} aria-label="Yield in cubic metres" />
            </label>
          </div>
        </div>
      </div>

      <p class="field__hint">
        Total counts kilograms and litres. Millilitre admixtures and the air content
        range are not masses and do not count, exactly as on the printed sheet.
      </p>

      <button class="btn btn--block" type="button" onclick={addMaterial}>Add material</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <label class="field">
        <span class="label">Slump</span>
        <input type="text" bind:value={slump} placeholder="180+/-20 mm" />
      </label>

      <label class="field">
        <span class="label">Specified compressive strength</span>
        <input type="text" bind:value={specifiedStrength} placeholder="5 MPa @ 7 days" />
      </label>

      <div class="grid2">
        <label class="field">
          <span class="label">Approx W/C ratio</span>
          <input type="text" inputmode="decimal" bind:value={wcRatio} placeholder="0.9" />
        </label>
        <label class="field">
          <span class="label">Max W/C ratio</span>
          <input type="text" inputmode="decimal" bind:value={wcRatioMax} placeholder="1.0" />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <label class="field">
        <span class="label">Notes</span>
        <textarea rows="5" bind:value={notes}></textarea>
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <button class="btn btn--block" type="button" disabled={printing} onclick={print}>
        {#if printing}<span class="spinner" aria-hidden="true"></span> Preparing…{:else}Print / Save as PDF{/if}
      </button>

      <button class="btn btn--primary btn--block btn--big" type="button" disabled={saving} onclick={save}>
        {#if saving}<span class="spinner" aria-hidden="true"></span> Saving…{:else}Save mix design{/if}
      </button>

      {#if savedId !== null}
        {#if confirmingDelete}
          <div class="banner banner--warn" role="status">
            <div class="spacer">
              <strong>Delete this mix design?</strong>
              This cannot be undone.
              <div class="banner__actions">
                <button class="btn btn--sm btn--danger" type="button" disabled={deleting} onclick={remove}>
                  {#if deleting}Deleting…{:else}Delete{/if}
                </button>
                <button class="btn btn--sm" type="button" onclick={() => (confirmingDelete = false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        {:else}
          <button class="btn btn--block" type="button" onclick={() => (confirmingDelete = true)}>
            Delete mix design
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  /* One shared column template for the header row and each one-line
     material row at 60rem+, so the two can't drift out of step with each
     other — but sharing the *string* isn't enough on its own. It used to
     end in `auto` for the actions column, sized to its content; that's
     fine within one row, but the header row's last cell is empty (no
     buttons there), so its `auto` track resolved to 0 and handed that
     width to the 1fr name column instead — the header's name column came
     out ~150px wider than a material row's, and every column after it
     inherited the same rightward shift. calc() against --tap here is
     content-independent: it computes the same number in every row
     regardless of what (if anything) that row puts in the track, so the
     shared template now guarantees identical column widths by
     construction instead of by coincidence. */
  .materials {
    --matcols: minmax(12rem, 1fr) 6rem 5.5rem 6.5rem 6.5rem calc(3 * var(--tap) + 0.6rem);
  }

  .materials__head {
    display: none;
  }

  .material {
    border-bottom: 1px solid var(--line-soft);
    padding: 0.85rem 0;
  }

  .material:first-child {
    padding-top: 0;
  }

  .material:last-of-type {
    border-bottom: 0;
  }

  .material__name {
    /* Tighter than the standalone .field spacing above — the small-field
       grid sits right underneath it, not a whole card section. */
    margin-bottom: 0.5rem;
  }

  /* auto-fit + minmax(min(9rem, 100%), 1fr) is what actually guarantees no
     side-scrolling here below 60rem: a plain minmax(9rem, 1fr) still forces
     a 9rem-wide track even in a column narrower than that, which is exactly
     the side-scroll this layout exists to remove. Wrapping the floor in
     min() caps it at the container's own width, so a track can always
     shrink to fit — on a phone that means every field ends up one to a
     line. (At 60rem and up the media query below replaces this grid with
     one line per material instead.) */
  .material__fields {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(9rem, 100%), 1fr));
    gap: 0.6rem;
  }

  .material__field {
    display: block;
    min-width: 0;
  }

  .material__field .label {
    margin-bottom: 0.25rem;
  }

  .material__total {
    margin: 0;
    min-height: var(--tap);
    display: flex;
    align-items: center;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .material__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
    margin-top: 0.6rem;
  }

  .material--total {
    border-top: 2px solid var(--line);
    padding-top: 0.9rem;
  }

  /* One line per material once there's room for it: the printed sheet is
     one row per material under one set of headings, and that's what lets
     you see the whole mix at a glance instead of scrolling past seven
     repeated MATERIAL/SSD PROPORTION/UNIT/... blocks. 60rem sits with
     comfortable slack under the office container's own 64rem ceiling — at
     that ceiling the name column alone gets roughly 400px, well above the
     12rem floor above it needs to hold everything else on one line — so
     ordinary scrollbars and browser chrome can't push this back into a
     side-scroll.

     The total/yield row opts out everywhere below (:not(.material--total))
     because it isn't "another material" the header row describes; it keeps
     its own compact two-field layout at every width instead of being
     forced into a five-column grid it doesn't belong in. */
  @media (min-width: 60rem) {
    .materials__head {
      display: grid;
      grid-template-columns: var(--matcols);
      column-gap: 0.6rem;
      padding: 0 0 0.4rem;
      border-bottom: 2px solid var(--line);
    }

    .materials__head span {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--ink-soft);
    }

    .material:not(.material--total) {
      display: grid;
      grid-template-columns: var(--matcols);
      column-gap: 0.6rem;
      align-items: center;
      padding: 0.4rem 0;
    }

    .material:not(.material--total) .material__name {
      margin-bottom: 0;
    }

    /* The wrapper drops out of layout so its four children — amount, unit,
       density, volume — take their place directly as columns 2-5 of the
       row's own grid, in the document order they already had. */
    .material:not(.material--total) .material__fields {
      display: contents;
    }

    /* The heading row above now names each column; showing a field's own
       caption too would say "Density" twice. The aria-label already on
       every input is what actually keeps the field identifiable once its
       heading is a whole row away rather than sitting right above it. */
    .material:not(.material--total) .material__field .label,
    .material:not(.material--total) .material__name .label {
      display: none;
    }

    .material:not(.material--total) .material__actions {
      justify-content: flex-start;
      margin-top: 0;
    }
  }
</style>
